import Phaser from 'phaser';
import { validateDeformClipSet, type DeformClipSet } from '@gooddesign/anim-schema';
import { CLIP_NAMES, type ClipName } from './clips';

// The real actor: a baked DeformClipSet exported from Marionette's warp solver
// (Order 15.7's deform core, driven by scripts/deform-bake-actor.ts).
//
// This replaces the procedural six-part placeholder. The runtime path is
// unchanged — MeshPlayer still only samples keys through playDeformTimeline —
// but the geometry is now a contour mesh traced from real PSD artwork, and the
// motion is a real warp solve rather than a sine wave.
//
// TWO THINGS THE CONTRACT DELIBERATELY DOESN'T CARRY, and so we do here:
//
//   1. WHERE THE ACTOR'S FEET ARE. Vertices are authored in the rig's own canvas
//      space (Photoshop pixels: origin top-left, y down, the figure ~12,700 px
//      tall somewhere inside it). The contract has no per-part transform and no
//      root offset — "part-local" means the shared rig space, nothing more. The
//      host has to decide what point of that space it plants at the actor's
//      feet. We translate the rest meshes once at load so the origin sits at
//      the bottom-centre of the figure, which is what Rig.place() expects.
//
//   2. HOW BIG THE ACTOR IS. Rig space is art space; world space is 640×360.
//      We derive a uniform scale from the figure's rest height.
//
// Offsets are RELATIVE, so translating rest vertices leaves every key untouched
// — but it must be applied to EVERY clip, not just the one whose geometry the
// player uploads: playDeformTimeline reads `clip.parts[i].mesh.vertices` of the
// clip it is sampling, so a clip left un-translated would snap on switch.

export interface ActorAsset {
  /** Namespaces this actor's Phaser texture keys. */
  key: string;
  /** Clip-set JSON, relative to `public/`. Part textures resolve relative to it. */
  url: string;
  /** Target on-screen height of the whole figure, world px, at rigScale 1. */
  figureHeightPx: number;
}

/** Ch_Third_Pig-Standing-6K — the slice's stand-in actor.
 *
 *  The plan calls the slice's actor a stand-in ("Master Skee isn't designed").
 *  Third Pig is the NK rig that can actually carry a platformer: separate Left
 *  Leg / Right Leg parts with real hip pivots, a Torso, and two arm chains. He
 *  is also the importer's own default rig.
 *
 *  He is a cartoon pig: the head is ~43% of his height and the legs are ~12%.
 *  `figureHeightPx` is therefore chosen so his TORSO AND LEGS span roughly the
 *  physics body (44 px) and the head overhangs it — the usual way a big-headed
 *  character is boxed, so you hit his body rather than the air above it.
 *
 *  FEEL: this is also the scale knob for the walk. He runs at MOVE.runSpeed but a
 *  cycle only strides ~9 world px at this size, so his feet skate. Drawing him
 *  larger makes the stride read and shrinks the skate as a fraction of the frame,
 *  without touching the deterministic physics. Tuned with Matt, 2026-07-09. */
export const THIRD_PIG: ActorAsset = {
  key: 'pig',
  url: 'actor/third-pig/clip-set.json',
  figureHeightPx: 104,
};

/** Phaser texture key for one part of one actor. Namespaced, so two actors can
 *  carry a part with the same rig key without colliding in the texture cache. */
export function partTextureKey(asset: ActorAsset, partKey: string): string {
  return `${asset.key}:${partKey}`;
}

/** A prepared actor plus the relative PNG paths its textures came from — both
 *  are needed: the path to fetch, the key to draw with. */
export interface PreparedActor extends LoadedActor {
  sourcePaths: string[];
}

export interface LoadedActor {
  /** Rest meshes translated to a feet-origin; textures rewritten to Phaser keys. */
  clips: DeformClipSet;
  /** Uniform rig→world scale. Pass to Rig/MeshPlayer as `scale`. */
  scale: number;
  /** Every Phaser texture key this actor's parts draw with, in paint order. */
  textureKeys: string[];
}

// LOADING IS TWO LOADER RUNS, AND THAT IS WHY THERE ARE TWO SCENES.
//
// The clip set is a manifest that names its own assets: you cannot queue the part
// PNGs until the JSON has been fetched and parsed. Phaser gives you exactly one
// loader run per Scene, between preload() and create(). So the dependency is
// resolved by ordering Scenes, not by fighting the loader:
//
//   PreloadScene  preload: the clip-set JSON   create: parse+normalize → registry
//   BootScene     preload: the 13 part PNGs    create: normal maps → start Alley
//
// The two shapes that look simpler both fail, and both failed here first:
//   • Queue the images from a `filecomplete-json-*` handler. They are registered
//     (totalToLoad rises) but never fetched: that event fires while the loader is
//     PROCESSING, not LOADING, so addFile parks them in `list` and nothing ever
//     pulls them. The loader wedges at 2/15 with zero failures.
//   • Restart the loader from create() and `scene.start()` from its COMPLETE
//     callback. The files do load, but the SceneManager silently drops a
//     scene.start() issued from there, so Boot never hands off.

/** Queue the clip-set manifest. Call from a Scene's preload(). */
export function preloadActorManifest(scene: Phaser.Scene, asset: ActorAsset): void {
  scene.load.json(jsonKey(asset), asset.url);
}

/** Parse, validate and normalize the manifest fetched by preloadActorManifest.
 *  Call from that Scene's create(). `required` narrows which clips the rig must
 *  carry — the combat set by default; an NPC rig passes its own (16.6). */
export function prepareActorFromCache(
  scene: Phaser.Scene,
  asset: ActorAsset,
  required: readonly string[] = CLIP_NAMES,
): PreparedActor {
  const raw = scene.cache.json.get(jsonKey(asset)) as DeformClipSet | undefined;
  if (!raw) throw new Error(`actor '${asset.key}': clip set not in cache; did preloadActorManifest() run?`);
  return prepareActor(raw, asset, required);
}

/** Queue the part PNGs the prepared manifest names. Call from the NEXT Scene's
 *  preload(), so they land in that Scene's own loader run. */
export function queueActorTextures(scene: Phaser.Scene, asset: ActorAsset, actor: PreparedActor): void {
  // Part textures are named relative to the clip set's own URL.
  const base = asset.url.slice(0, asset.url.lastIndexOf('/') + 1);
  actor.sourcePaths.forEach((src, i) => scene.load.image(actor.textureKeys[i], base + src));
}

const jsonKey = (asset: ActorAsset) => `${asset.key}:clip-set`;

/** Normalize a freshly-parsed clip set: validate, translate to a feet-origin,
 *  compute the world scale, and rewrite `mesh.texture` from a relative PNG path
 *  to the Phaser texture key the MeshPlayer will look up. Returns the original
 *  relative paths alongside, so the caller can queue the image loads. */
export function prepareActor(
  raw: DeformClipSet,
  asset: ActorAsset,
  required: readonly string[] = CLIP_NAMES,
): PreparedActor {
  // The bake validates before writing; validate again on the way in. A clip set
  // that lost its part signature in transit draws silent garbage, not an error.
  const problems = validateDeformClipSet(raw);
  if (problems.length > 0) {
    throw new Error(`actor '${asset.key}': invalid clip set:\n  ${problems.join('\n  ')}`);
  }

  const missing = required.filter((n) => !raw.clips[n]);
  if (missing.length > 0) {
    throw new Error(`actor '${asset.key}': clip set is missing ${missing.join(', ')}`);
  }

  const template = raw.clips[raw.default_clip ?? 'idle'];
  if (!template) throw new Error(`actor '${asset.key}': default_clip names a clip that isn't there`);

  // ── Feet origin ────────────────────────────────────────────────────────────
  // The figure's bottom edge, and the horizontal centre of the geometry sitting
  // on it. Centring on the FEET rather than the whole bounding box matters: this
  // pig's ears and snout push the bbox sideways, and centring on that would have
  // him walking with one shoulder ahead of his hips.
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const track of template.parts) {
    for (const v of track.mesh.vertices) {
      if (v.x < minX) minX = v.x;
      if (v.x > maxX) maxX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.y > maxY) maxY = v.y;
    }
  }
  const figureHeight = maxY - minY;
  if (!(figureHeight > 0)) throw new Error(`actor '${asset.key}': degenerate rest geometry`);

  const footBand = maxY - figureHeight * 0.04;
  let footSum = 0, footCount = 0;
  for (const track of template.parts) {
    for (const v of track.mesh.vertices) {
      if (v.y >= footBand) { footSum += v.x; footCount++; }
    }
  }
  const originX = footCount > 0 ? footSum / footCount : (minX + maxX) / 2;
  const originY = maxY;

  // Translate EVERY clip's rest mesh (see the header note). Offsets are relative
  // and so are untouched.
  for (const clip of Object.values(raw.clips)) {
    for (const track of clip.parts) {
      for (const v of track.mesh.vertices) {
        v.x -= originX;
        v.y -= originY;
      }
    }
  }

  // ── Textures ───────────────────────────────────────────────────────────────
  const sourcePaths: string[] = [];
  const textureKeys: string[] = [];
  for (const track of template.parts) {
    const src = track.mesh.texture;
    if (!src) throw new Error(`actor '${asset.key}': part '${track.mesh.part_key}' has no texture`);
    sourcePaths.push(src);
    textureKeys.push(partTextureKey(asset, track.mesh.part_key));
  }
  // Rewrite on every clip: MeshPlayer reads the template, but a future consumer
  // reading any clip should see the same key.
  for (const clip of Object.values(raw.clips)) {
    clip.parts.forEach((track, i) => { track.mesh.texture = textureKeys[i]; });
  }

  return {
    clips: raw,
    scale: asset.figureHeightPx / figureHeight,
    textureKeys,
    sourcePaths,
  };
}

export type { ClipName };
